/**
 * 装配体 Mate Definition 草稿服务。
 *
 * 文件职责：
 * 1. 根据控制器状态创建当前 Mate Definition；
 * 2. 解析 Distance Mate 默认尺寸和编辑态距离条件；
 * 3. 为 MateEntityRef 附加选择瞬间组件位姿。
 */

import {
  ASSEMBLY_MATE_SCHEMA_VERSION,
  AssemblyMateType,
  type MateDistanceEntityModes,
  MateDistanceMode,
  MateSolveStatus,
  cloneComponentTransformSnapshot,
  type AssemblyMateDefinition,
  type MateConcentricMisalignment,
  type MateDimension,
  type MateEntityRef,
  type MateLockedRelativeTransform,
} from '../definition';
import {
  measureStandardMateDistanceDimensionValue,
  resolveDefaultStandardMateDistanceEntityModes,
  resolveDefaultStandardMateDistanceMode,
  resolveStandardMateDistanceEntityModeOptions,
  resolveStandardMateDistanceModeOptions,
} from '../solver';
import { isAssemblyMateTypeEnabled } from '../compatibility';
import {
  resolveMateCandidatePair,
  type AssemblyMateSelectionCandidate,
} from '../selection';
import type { AssemblyMateDocumentModel } from '../document';
import {
  composeAssemblyMateTransformMatrix,
  decomposeAssemblyMateTransformMatrix,
} from './support/AssemblyMateTransformMatrix';
import { createAssemblyMateAutoAngleReferenceEntity, isAssemblyMateAutoAngleReferenceEntity } from '../definition/AssemblyMateAngleReference';
import type { AssemblyMateControllerRuntime } from './AssemblyMateControllerRuntime';
import type { AssemblyMateControllerState } from './AssemblyMateControllerTypes';

/**
 * 装配体 Mate Definition 草稿服务。
 */
export class AssemblyMateDefinitionDraftService {
  private readonly runtime: AssemblyMateControllerRuntime;

  /**
   * 构造函数。
   *
   * @param runtime 控制器运行时服务。
   */
  public constructor(runtime: AssemblyMateControllerRuntime) {
    this.runtime = runtime;
  }

  /**
   * 解析 Distance Mate 的当前默认尺寸补丁。
   *
   * @param mateType 当前配合类型。
   * @param candidates 当前选择候选。
   * @param state 当前控制器状态。
   * @param options 尺寸刷新选项。
   * @returns 可合并到控制器状态中的尺寸补丁。
   */
  public resolveMeasuredDistancePatch(
    mateType: AssemblyMateType | null,
    candidates: readonly AssemblyMateSelectionCandidate[],
    state: AssemblyMateControllerState,
    options: {
      /** 是否保留当前距离数值，仅刷新距离条件与可用性。 */
      readonly preserveCurrentDistance?: boolean;
      /** SOLIDWORKS 会在圆柱类 Distance 中记住用户上次使用的距离条件。 */
      readonly preferredDistanceMode?: MateDistanceMode | null;
      /** SOLIDWORKS 会在圆柱类 Distance 中记住用户上次使用的逐实体条件。 */
      readonly preferredDistanceEntityModes?: MateDistanceEntityModes | null;
    } = {},
  ): Partial<AssemblyMateControllerState> {
    if (mateType !== AssemblyMateType.Distance) {
      return {};
    }
    const pair = resolveMateCandidatePair(candidates);
    if (!pair) {
      return {};
    }
    const distanceModeOptions = resolveStandardMateDistanceModeOptions(
      pair[0].entityRef,
      pair[1].entityRef,
    );
    const distanceEntityModeOptions = resolveStandardMateDistanceEntityModeOptions(
      pair[0].entityRef,
      pair[1].entityRef,
    );
    const defaultDistanceMode = resolveDefaultStandardMateDistanceMode(
      pair[0].entityRef,
      pair[1].entityRef,
    );
    const defaultDistanceEntityModes = resolveDefaultStandardMateDistanceEntityModes(
      pair[0].entityRef,
      pair[1].entityRef,
    );
    const preferredDistanceMode = options.preferredDistanceMode ?? null;
    const preferredDistanceEntityModes = options.preferredDistanceEntityModes ?? null;
    const canUseEntityMode = (
      modeOptions: readonly { readonly mode: MateDistanceMode; readonly enabled: boolean }[],
      mode: MateDistanceMode,
    ): boolean => (
      modeOptions.length === 0
      || modeOptions.some((item) => item.mode === mode && item.enabled)
    );
    const shouldKeepCurrentMode = state.selectedMateType === AssemblyMateType.Distance
      && distanceModeOptions.some((item) => item.mode === state.distanceMode && item.enabled);
    const shouldKeepCurrentEntityModes = state.selectedMateType === AssemblyMateType.Distance
      && canUseEntityMode(distanceEntityModeOptions.firstEntityOptions, state.distanceEntityModes.firstEntityMode)
      && canUseEntityMode(distanceEntityModeOptions.secondEntityOptions, state.distanceEntityModes.secondEntityMode);
    const canUsePreferredMode = !shouldKeepCurrentMode
      && preferredDistanceMode !== null
      && distanceModeOptions.some((item) => item.mode === preferredDistanceMode && item.enabled);
    const canUsePreferredEntityModes = !shouldKeepCurrentEntityModes
      && preferredDistanceEntityModes !== null
      && canUseEntityMode(distanceEntityModeOptions.firstEntityOptions, preferredDistanceEntityModes.firstEntityMode)
      && canUseEntityMode(distanceEntityModeOptions.secondEntityOptions, preferredDistanceEntityModes.secondEntityMode);
    const distanceMode = shouldKeepCurrentMode
      ? state.distanceMode
      : canUsePreferredMode
        ? preferredDistanceMode
        : defaultDistanceMode;
    const distanceEntityModes = shouldKeepCurrentEntityModes
      ? state.distanceEntityModes
      : canUsePreferredEntityModes
        ? preferredDistanceEntityModes
        : defaultDistanceEntityModes;
    if (options.preserveCurrentDistance && Number.isFinite(state.distance)) {
      return {
        distanceMode,
        distanceEntityModes,
        distanceModeOptions,
        firstDistanceModeOptions: distanceEntityModeOptions.firstEntityOptions,
        secondDistanceModeOptions: distanceEntityModeOptions.secondEntityOptions,
      };
    }
    const distance = measureStandardMateDistanceDimensionValue(
      pair[0].entityRef,
      pair[1].entityRef,
      undefined,
      distanceMode,
      distanceEntityModes,
    );
    return typeof distance === 'number' && Number.isFinite(distance)
      ? {
          distance,
          distanceMode,
          distanceEntityModes,
          distanceModeOptions,
          firstDistanceModeOptions: distanceEntityModeOptions.firstEntityOptions,
          secondDistanceModeOptions: distanceEntityModeOptions.secondEntityOptions,
        }
      : {
          distanceMode,
          distanceEntityModes,
          distanceModeOptions,
          firstDistanceModeOptions: distanceEntityModeOptions.firstEntityOptions,
          secondDistanceModeOptions: distanceEntityModeOptions.secondEntityOptions,
        };
  }

  /**
   * 从既有 Distance Mate 恢复距离条件状态。
   *
   * @param mate 正在编辑的 Mate。
   * @param candidates 已恢复的两个配合候选。
   * @returns 可合并到控制器状态的距离补丁。
   */
  public resolveExistingDistancePatch(
    mate: AssemblyMateDefinition,
    candidates: readonly AssemblyMateSelectionCandidate[],
  ): Partial<AssemblyMateControllerState> {
    if (mate.mateType !== AssemblyMateType.Distance) {
      return {
        distanceMode: MateDistanceMode.Minimum,
        distanceEntityModes: {
          firstEntityMode: MateDistanceMode.Minimum,
          secondEntityMode: MateDistanceMode.Minimum,
        },
        distanceModeOptions: [],
        firstDistanceModeOptions: [],
        secondDistanceModeOptions: [],
      };
    }

    const pair = resolveMateCandidatePair(candidates);
    if (!pair) {
      return {
        distanceMode: MateDistanceMode.Minimum,
        distanceEntityModes: {
          firstEntityMode: MateDistanceMode.Minimum,
          secondEntityMode: MateDistanceMode.Minimum,
        },
        distanceModeOptions: [],
        firstDistanceModeOptions: [],
        secondDistanceModeOptions: [],
      };
    }

    const distanceModeOptions = resolveStandardMateDistanceModeOptions(
      pair[0].entityRef,
      pair[1].entityRef,
    );
    const defaultDistanceMode = resolveDefaultStandardMateDistanceMode(
      pair[0].entityRef,
      pair[1].entityRef,
    );
    const distanceEntityModeOptions = resolveStandardMateDistanceEntityModeOptions(
      pair[0].entityRef,
      pair[1].entityRef,
    );
    const defaultDistanceEntityModes = resolveDefaultStandardMateDistanceEntityModes(
      pair[0].entityRef,
      pair[1].entityRef,
    );
    const savedDistanceMode = mate.dimension?.distanceMode ?? defaultDistanceMode;
    const savedDistanceEntityModes = mate.dimension?.distanceEntityModes ?? defaultDistanceEntityModes;
    const canUseSavedMode = distanceModeOptions.length === 0
      || distanceModeOptions.some((option) => option.mode === savedDistanceMode && option.enabled);
    const canUseEntityMode = (
      modeOptions: readonly { readonly mode: MateDistanceMode; readonly enabled: boolean }[],
      mode: MateDistanceMode,
    ): boolean => (
      modeOptions.length === 0
      || modeOptions.some((option) => option.mode === mode && option.enabled)
    );
    const canUseSavedEntityModes = canUseEntityMode(
      distanceEntityModeOptions.firstEntityOptions,
      savedDistanceEntityModes.firstEntityMode,
    ) && canUseEntityMode(
      distanceEntityModeOptions.secondEntityOptions,
      savedDistanceEntityModes.secondEntityMode,
    );

    return {
      distanceMode: canUseSavedMode ? savedDistanceMode : defaultDistanceMode,
      distanceEntityModes: canUseSavedEntityModes ? savedDistanceEntityModes : defaultDistanceEntityModes,
      distanceModeOptions,
      firstDistanceModeOptions: distanceEntityModeOptions.firstEntityOptions,
      secondDistanceModeOptions: distanceEntityModeOptions.secondEntityOptions,
    };
  }

  /**
   * 创建当前 Mate Definition。
   *
   * @param state 当前控制器状态。
   * @param mateDocument Mate 文档。
   * @returns Mate Definition；条件不足时返回 null。
   */
  public createCurrentMateDefinition(
    state: AssemblyMateControllerState,
    mateDocument: AssemblyMateDocumentModel,
  ): AssemblyMateDefinition | null {
    const pair = resolveMateCandidatePair(state.candidates);
    const mateType = state.selectedMateType;
    if (!pair || !mateType || !state.compatibility) {
      return null;
    }
    if (!isAssemblyMateTypeEnabled(state.compatibility, mateType)) {
      return null;
    }

    const now = Date.now();
    const dimension = this.createMateDimension(state, mateType);
    const lockedRelativeTransform = mateType === AssemblyMateType.Lock
      ? this.createLockedRelativeTransform(pair)
      : undefined;
    const concentricMisalignment = this.createConcentricMisalignment(state, mateType);
    const firstEntity = this.withSelectionComponentTransform(pair[0]);
    const secondEntity = this.withSelectionComponentTransform(pair[1]);
    const existingMate = state.editingMateId
      ? mateDocument.mates.find((mate) => mate.mateId === state.editingMateId) ?? null
      : null;
    const referenceEntity = mateType === AssemblyMateType.Angle
      ? state.angleReferenceEntity
        ?? this.resolveEditingAutoAngleReferenceEntity(existingMate, firstEntity, secondEntity)
        ?? createAssemblyMateAutoAngleReferenceEntity(firstEntity, secondEntity)
      : undefined;

    return {
      schemaVersion: ASSEMBLY_MATE_SCHEMA_VERSION,
      mateId: existingMate?.mateId ?? state.draftMateId,
      assemblyId: mateDocument.assemblyId,
      mateType,
      firstEntity,
      secondEntity,
      ...(referenceEntity ? { referenceEntity } : {}),
      alignment: state.alignment,
      ...(dimension ? { dimension } : {}),
      lockRotation: mateType === AssemblyMateType.Concentric && state.lockRotationEnabled
        ? state.lockRotation
        : undefined,
      ...(concentricMisalignment ? { concentricMisalignment } : {}),
      ...(lockedRelativeTransform ? { lockedRelativeTransform } : {}),
      suppressed: existingMate?.suppressed ?? false,
      solveStatus: MateSolveStatus.NotRun,
      createdAt: existingMate?.createdAt ?? now,
      updatedAt: now,
    };
  }

  /**
   * 为 Mate 实体附加选择瞬间的组件位姿。
   *
   * @param candidate Mate 选择候选。
   * @returns 带选择位姿基线的 Mate 实体引用。
   */
  public withSelectionComponentTransform(candidate: AssemblyMateSelectionCandidate): MateEntityRef {
    const componentState = this.runtime.resolveComponentState(candidate.entityRef.componentInstanceId);
    if (candidate.entityRef.componentTransformAtSelection) {
      return {
        ...candidate.entityRef,
        componentTransformAtSelection: cloneComponentTransformSnapshot(candidate.entityRef.componentTransformAtSelection),
        ...(candidate.entityRef.componentWorldTransformAtSelection
          ? {
              componentWorldTransformAtSelection: cloneComponentTransformSnapshot(
                candidate.entityRef.componentWorldTransformAtSelection,
              ),
            }
          : componentState?.worldTransform
            ? { componentWorldTransformAtSelection: cloneComponentTransformSnapshot(componentState.worldTransform) }
            : {}),
      };
    }

    if (!componentState) {
      return candidate.entityRef;
    }

    return {
      ...candidate.entityRef,
      componentTransformAtSelection: cloneComponentTransformSnapshot(componentState.transform),
      ...(componentState.worldTransform
        ? { componentWorldTransformAtSelection: cloneComponentTransformSnapshot(componentState.worldTransform) }
        : {}),
    };
  }

  /**
   * 解析编辑态可复用的自动角度参考轴。
   *
   * @param existingMate 正在编辑的原 Mate。
   * @param firstEntity 当前第一实体。
   * @param secondEntity 当前第二实体。
   * @returns 可复用的自动参考轴；不适用时返回 undefined。
   */
  private resolveEditingAutoAngleReferenceEntity(
    existingMate: AssemblyMateDefinition | null,
    firstEntity: MateEntityRef,
    secondEntity: MateEntityRef,
  ): MateEntityRef | undefined {
    if (
      !existingMate
      || existingMate.mateType !== AssemblyMateType.Angle
      || !existingMate.referenceEntity
      || !isAssemblyMateAutoAngleReferenceEntity(existingMate.referenceEntity)
    ) {
      return undefined;
    }
    if (
      existingMate.firstEntity.stableRef !== firstEntity.stableRef
      || existingMate.secondEntity.stableRef !== secondEntity.stableRef
    ) {
      return undefined;
    }
    return existingMate.referenceEntity;
  }

  /**
   * 创建 Mate 尺寸。
   *
   * @param state 当前控制器状态。
   * @param mateType 配合类型。
   * @returns 尺寸定义。
   */
  private createMateDimension(
    state: AssemblyMateControllerState,
    mateType: AssemblyMateType,
  ): MateDimension | undefined {
    if (mateType === AssemblyMateType.Distance) {
      return {
        kind: AssemblyMateType.Distance,
        value: state.distance,
        flipped: state.dimensionFlipped,
        distanceMode: state.distanceMode,
        distanceEntityModes: state.distanceEntityModes,
      };
    }
    if (mateType === AssemblyMateType.Angle) {
      return {
        kind: AssemblyMateType.Angle,
        value: state.angle,
        flipped: state.dimensionFlipped,
      };
    }
    return undefined;
  }

  /**
   * 创建 Concentric mate 的错位参数。
   *
   * @param state 当前控制器状态。
   * @param mateType 当前 Mate 类型。
   * @returns 错位同轴心参数；未启用或条件不足时返回 undefined。
   */
  private createConcentricMisalignment(
    state: AssemblyMateControllerState,
    mateType: AssemblyMateType,
  ): MateConcentricMisalignment | undefined {
    if (
      mateType !== AssemblyMateType.Concentric
      || !state.concentricMisalignmentEnabled
      || !state.concentricMisalignmentLinkedMateId
      || !Number.isFinite(state.concentricMisalignmentMaximumDeviation)
      || state.concentricMisalignmentMaximumDeviation < 0
    ) {
      return undefined;
    }

    return {
      type: state.concentricMisalignmentType,
      linkedMateId: state.concentricMisalignmentLinkedMateId,
      maximumDeviation: state.concentricMisalignmentMaximumDeviation,
    };
  }

  /**
   * 创建 Lock mate 的第一组件到第二组件相对位姿。
   *
   * @param pair 当前两个候选。
   * @returns 相对位姿；组件缺失时返回 undefined。
   */
  private createLockedRelativeTransform(
    pair: readonly [AssemblyMateSelectionCandidate, AssemblyMateSelectionCandidate],
  ): MateLockedRelativeTransform | undefined {
    const firstComponent = this.runtime.resolveComponentState(pair[0].entityRef.componentInstanceId);
    const secondComponent = this.runtime.resolveComponentState(pair[1].entityRef.componentInstanceId);
    if (!firstComponent || !secondComponent) {
      return undefined;
    }

    const firstMatrixInverse = composeAssemblyMateTransformMatrix(firstComponent.transform).invert();
    const secondMatrix = composeAssemblyMateTransformMatrix(secondComponent.transform);
    return {
      transform: decomposeAssemblyMateTransformMatrix(firstMatrixInverse.multiply(secondMatrix)),
    };
  }
}
